import { z } from 'zod';

export const UI_CONSULTATION_KINDS = [
  'ui_interpretation',
  'ui_critique',
  'information_architecture_review',
  'route_design_review',
] as const;

export const UI_CONSULTATION_STATUSES = [
  'draft',
  'running',
  'completed',
  'degraded_completed',
  'capability_blocked',
  'failed',
] as const;

export const UI_CONSULTATION_ACE_BLOCK_KINDS = [
  'functionality',
  'hooks',
  'rtk_contract',
  'domain_model',
  'prd_contract',
] as const;

const normalizeString = (value: unknown): string => String(value ?? '').trim();

const normalizeStringArray = (values: unknown): string[] =>
  Array.isArray(values)
    ? values
        .map((value) => normalizeString(value))
        .filter(Boolean)
        .sort((left, right) => left.localeCompare(right))
    : [];

const normalizeOrderedStringArray = (values: unknown): string[] =>
  Array.isArray(values)
    ? values
        .map((value) => normalizeString(value))
        .filter(Boolean)
    : [];

const stableValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map((entry) => stableValue(entry));
  }
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, entry]) => entry !== undefined)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, entry]) => [key, stableValue(entry)]);
    return Object.fromEntries(entries);
  }
  return value;
};

const stableStringify = (value: unknown): string => JSON.stringify(stableValue(value), null, 2);

const estimateTokens = (value: unknown): number =>
  Math.max(1, Math.ceil(stableStringify(value).length / 4));

const stableHash = (value: string): string => {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `fnv1a-${(hash >>> 0).toString(16).padStart(8, '0')}`;
};

export const UiConsultationTargetSchema = z.object({
  appId: z.string().min(1),
  route: z.string().min(1),
  persona: z.string().min(1),
  packageName: z.string().min(1),
  componentName: z.string().min(1).optional(),
  owner: z.string().min(1).optional(),
  moduleId: z.string().min(1).optional(),
  routeManifest: z.record(z.string(), z.unknown()).default({}),
  metadata: z.record(z.string(), z.unknown()).default({}),
});

export const UiConsultationRequirementExcerptSchema = z.object({
  sourcePath: z.string().min(1),
  summary: z.string().min(1),
  excerpt: z.string().min(1).optional(),
  authorityRole: z.string().min(1).optional(),
  priority: z.enum(['required', 'supporting']).default('required'),
});

export const UiConsultationAstNodeDigestSchema = z.object({
  kind: z.string().min(1),
  label: z.string().min(1),
  path: z.string().min(1).optional(),
  props: z.array(z.string().min(1)).default([]),
  testIds: z.array(z.string().min(1)).default([]),
  children: z.array(z.string().min(1)).default([]),
});

export const UiConsultationAstDigestSchema = z.object({
  sourcePath: z.string().min(1),
  rootKind: z.string().min(1),
  nodeCount: z.number().int().nonnegative(),
  branchCount: z.number().int().nonnegative().default(0),
  actionIds: z.array(z.string().min(1)).default([]),
  controlIds: z.array(z.string().min(1)).default([]),
  nodes: z.array(UiConsultationAstNodeDigestSchema).default([]),
  metadata: z.record(z.string(), z.unknown()).default({}),
});

export const UiConsultationObjectTreeDigestSchema = z.object({
  rootType: z.string().min(1),
  nodeCount: z.number().int().nonnegative(),
  summary: z.string().min(1),
  sample: z.record(z.string(), z.unknown()).default({}),
  keys: z.array(z.string().min(1)).default([]),
  actions: z.array(z.string().min(1)).default([]),
  testIds: z.array(z.string().min(1)).default([]),
  metadata: z.record(z.string(), z.unknown()).default({}),
});

export const UiConsultationRuntimeDigestSchema = z.object({
  captureMode: z.enum(['none', 'snapshot', 'live']).default('none'),
  accessibilitySummary: z.string().default(''),
  visibleTextBlocks: z.array(z.string().min(1)).default([]),
  layoutMarkers: z.array(z.string().min(1)).default([]),
  actionLabels: z.array(z.string().min(1)).default([]),
  testIds: z.array(z.string().min(1)).default([]),
  objectTree: z.record(z.string(), z.unknown()).default({}),
  metadata: z.record(z.string(), z.unknown()).default({}),
});

export const UiConsultationAceBlockSchema = z.object({
  id: z.string().min(1),
  kind: z.enum(UI_CONSULTATION_ACE_BLOCK_KINDS),
  title: z.string().min(1),
  summary: z.string().min(1),
  bullets: z.array(z.string().min(1)).default([]),
  sourceRefs: z.array(z.string().min(1)).default([]),
  evidenceIds: z.array(z.string().min(1)).default([]),
});

export const UiConsultationEvidenceEntrySchema = z.object({
  id: z.string().min(1),
  section: z.enum([
    'objective',
    'requirements',
    'target_identity',
    'ast_digest',
    'object_tree',
    'runtime_digest',
    'ace_blocks',
  ]),
  label: z.string().min(1),
  sourcePath: z.string().min(1).optional(),
});

export const UiConsultationBudgetSchema = z.object({
  softTokenTarget: z.number().int().positive().default(55000),
  hardTokenCap: z.number().int().positive().default(70000),
  sectionBudgets: z
    .object({
      requirements: z.number().min(0).max(1).default(0.15),
      targetIdentity: z.number().min(0).max(1).default(0.05),
      astDigest: z.number().min(0).max(1).default(0.3),
      objectTree: z.number().min(0).max(1).default(0.25),
      runtimeDigest: z.number().min(0).max(1).default(0.2),
      aceBlocks: z.number().min(0).max(1).default(0.15),
      operatorQuestion: z.number().min(0).max(1).default(0.05),
    })
    .default({
      requirements: 0.15,
      targetIdentity: 0.05,
      astDigest: 0.3,
      objectTree: 0.25,
      runtimeDigest: 0.2,
      aceBlocks: 0.15,
      operatorQuestion: 0.05,
    }),
});

export const UiConsultationGroundingManifestSchema = z.object({
  schemaVersion: z.literal(1),
  consultationKind: z.enum(UI_CONSULTATION_KINDS),
  objective: z.string().min(1),
  question: z.string().min(1),
  target: UiConsultationTargetSchema,
  requirements: z.array(UiConsultationRequirementExcerptSchema).default([]),
  aceBlocks: z.array(UiConsultationAceBlockSchema).default([]),
  astDigest: UiConsultationAstDigestSchema,
  objectTreeDigest: UiConsultationObjectTreeDigestSchema,
  runtimeDigest: UiConsultationRuntimeDigestSchema,
  evidenceMap: z.array(UiConsultationEvidenceEntrySchema).default([]),
  metadata: z.record(z.string(), z.unknown()).default({}),
});

export const UiConsultationBudgetReportSchema = z.object({
  schemaVersion: z.literal(1),
  totalEstimatedTokens: z.number().int().nonnegative(),
  softTokenTarget: z.number().int().positive(),
  hardTokenCap: z.number().int().positive(),
  withinSoftTarget: z.boolean(),
  withinHardCap: z.boolean(),
  trimmedSections: z.array(z.string().min(1)).default([]),
  sectionEstimates: z.record(z.string(), z.number().int().nonnegative()),
  manifestHash: z.string().min(1),
});

export const UiConsultationResponseSchema = z.object({
  summary: z.string().min(1),
  surfaceUnderstanding: z.array(z.string().min(1)).default([]),
  strengths: z.array(z.string().min(1)).default([]),
  failuresOrRisks: z.array(z.string().min(1)).default([]),
  interactionRecommendations: z.array(z.string().min(1)).default([]),
  informationArchitectureRecommendations: z.array(z.string().min(1)).default([]),
  visualHierarchyRecommendations: z.array(z.string().min(1)).default([]),
  componentLevelChanges: z.array(z.string().min(1)).default([]),
  dataBindingConcerns: z.array(z.string().min(1)).default([]),
  acceptanceChecks: z.array(z.string().min(1)).default([]),
  confidence: z.enum(['low', 'medium', 'high']).default('medium'),
  citations: z.array(z.string().min(1)).default([]),
});

export const UiConsultationRunRequestSchema = z.object({
  consultationKind: z.enum(UI_CONSULTATION_KINDS),
  target: UiConsultationTargetSchema,
  question: z.string().min(1),
  grounding: UiConsultationGroundingManifestSchema,
  budget: UiConsultationBudgetSchema.default(() => DEFAULT_UI_CONSULTATION_BUDGET),
  artifactHints: z
    .object({
      sessionId: z.string().min(1).optional(),
      requestedBy: z.string().min(1).optional(),
      lane: z.string().min(1).optional(),
    })
    .default({}),
});

export type UiConsultationKind = z.infer<typeof UiConsultationRunRequestSchema>['consultationKind'];
export type UiConsultationTarget = z.infer<typeof UiConsultationTargetSchema>;
export type UiConsultationRequirementExcerpt = z.infer<typeof UiConsultationRequirementExcerptSchema>;
export type UiConsultationAstDigest = z.infer<typeof UiConsultationAstDigestSchema>;
export type UiConsultationObjectTreeDigest = z.infer<typeof UiConsultationObjectTreeDigestSchema>;
export type UiConsultationRuntimeDigest = z.infer<typeof UiConsultationRuntimeDigestSchema>;
export type UiConsultationAceBlock = z.infer<typeof UiConsultationAceBlockSchema>;
export type UiConsultationGroundingManifest = z.infer<typeof UiConsultationGroundingManifestSchema>;
export type UiConsultationBudget = z.infer<typeof UiConsultationBudgetSchema>;
export type UiConsultationBudgetReport = z.infer<typeof UiConsultationBudgetReportSchema>;
export type UiConsultationResponse = z.infer<typeof UiConsultationResponseSchema>;
export type UiConsultationRunRequest = z.infer<typeof UiConsultationRunRequestSchema>;

export type BuildUiConsultationGroundingManifestInput = {
  consultationKind: UiConsultationKind;
  objective: string;
  question: string;
  target: UiConsultationTarget;
  requirements?: UiConsultationRequirementExcerpt[];
  aceBlocks?: UiConsultationAceBlock[];
  astDigest: UiConsultationAstDigest;
  objectTreeDigest: UiConsultationObjectTreeDigest;
  runtimeDigest?: Partial<UiConsultationRuntimeDigest>;
  evidenceMap?: Array<z.infer<typeof UiConsultationEvidenceEntrySchema>>;
  metadata?: Record<string, unknown>;
};

export const DEFAULT_UI_CONSULTATION_BUDGET: UiConsultationBudget =
  UiConsultationBudgetSchema.parse({});

export const buildUiConsultationGroundingManifest = (
  input: BuildUiConsultationGroundingManifestInput,
): UiConsultationGroundingManifest => {
  const manifest = {
    schemaVersion: 1 as const,
    consultationKind: input.consultationKind,
    objective: normalizeString(input.objective),
    question: normalizeString(input.question),
    target: {
      ...input.target,
      appId: normalizeString(input.target.appId),
      route: normalizeString(input.target.route),
      persona: normalizeString(input.target.persona),
      packageName: normalizeString(input.target.packageName),
      componentName: normalizeString(input.target.componentName) || undefined,
      owner: normalizeString(input.target.owner) || undefined,
      moduleId: normalizeString(input.target.moduleId) || undefined,
      metadata: stableValue(input.target.metadata ?? {}) as Record<string, unknown>,
      routeManifest: stableValue(input.target.routeManifest ?? {}) as Record<string, unknown>,
    },
    requirements: (input.requirements ?? []).map((entry) => ({
      sourcePath: normalizeString(entry.sourcePath),
      summary: normalizeString(entry.summary),
      excerpt: normalizeString(entry.excerpt) || undefined,
      authorityRole: normalizeString(entry.authorityRole) || undefined,
      priority: entry.priority ?? 'required',
    })),
    aceBlocks: (input.aceBlocks ?? []).map((entry) => ({
      id: normalizeString(entry.id),
      kind: entry.kind,
      title: normalizeString(entry.title),
      summary: normalizeString(entry.summary),
      bullets: normalizeOrderedStringArray(entry.bullets),
      sourceRefs: normalizeOrderedStringArray(entry.sourceRefs),
      evidenceIds: normalizeOrderedStringArray(entry.evidenceIds),
    })),
    astDigest: {
      ...input.astDigest,
      actionIds: normalizeStringArray(input.astDigest.actionIds),
      controlIds: normalizeStringArray(input.astDigest.controlIds),
      nodes: [...(input.astDigest.nodes ?? [])].map((node) => ({
        ...node,
        props: normalizeStringArray(node.props),
        testIds: normalizeStringArray(node.testIds),
        children: normalizeStringArray(node.children),
      })),
      metadata: stableValue(input.astDigest.metadata ?? {}) as Record<string, unknown>,
    },
    objectTreeDigest: {
      ...input.objectTreeDigest,
      keys: normalizeStringArray(input.objectTreeDigest.keys),
      actions: normalizeStringArray(input.objectTreeDigest.actions),
      testIds: normalizeStringArray(input.objectTreeDigest.testIds),
      sample: stableValue(input.objectTreeDigest.sample ?? {}) as Record<string, unknown>,
      metadata: stableValue(input.objectTreeDigest.metadata ?? {}) as Record<string, unknown>,
    },
    runtimeDigest: {
      captureMode: input.runtimeDigest?.captureMode ?? 'none',
      accessibilitySummary: normalizeString(input.runtimeDigest?.accessibilitySummary),
      visibleTextBlocks: normalizeStringArray(input.runtimeDigest?.visibleTextBlocks),
      layoutMarkers: normalizeStringArray(input.runtimeDigest?.layoutMarkers),
      actionLabels: normalizeStringArray(input.runtimeDigest?.actionLabels),
      testIds: normalizeStringArray(input.runtimeDigest?.testIds),
      objectTree: stableValue(input.runtimeDigest?.objectTree ?? {}) as Record<string, unknown>,
      metadata: stableValue(input.runtimeDigest?.metadata ?? {}) as Record<string, unknown>,
    },
    evidenceMap: [...(input.evidenceMap ?? [])].map((entry) => ({
      ...entry,
      id: normalizeString(entry.id),
      label: normalizeString(entry.label),
      sourcePath: normalizeString(entry.sourcePath) || undefined,
    })),
    metadata: stableValue(input.metadata ?? {}) as Record<string, unknown>,
  };

  return UiConsultationGroundingManifestSchema.parse(manifest);
};

const manifestSectionEstimates = (manifest: UiConsultationGroundingManifest) => ({
  objective: estimateTokens({ objective: manifest.objective, question: manifest.question }),
  requirements: estimateTokens(manifest.requirements),
  targetIdentity: estimateTokens(manifest.target),
  astDigest: estimateTokens(manifest.astDigest),
  objectTree: estimateTokens(manifest.objectTreeDigest),
  runtimeDigest: estimateTokens(manifest.runtimeDigest),
  aceBlocks: estimateTokens(manifest.aceBlocks),
  evidenceMap: estimateTokens(manifest.evidenceMap),
});

const dedupeRequirements = (
  requirements: UiConsultationGroundingManifest['requirements'],
): UiConsultationGroundingManifest['requirements'] => {
  const seen = new Set<string>();
  return requirements.filter((entry) => {
    const key = `${entry.sourcePath}::${entry.summary}::${entry.excerpt ?? ''}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const trimAceBlockBullets = (
  aceBlocks: UiConsultationGroundingManifest['aceBlocks'],
  maxBulletsPerBlock: number,
): UiConsultationGroundingManifest['aceBlocks'] =>
  aceBlocks.map((block) => ({
    ...block,
    bullets: block.bullets.slice(0, maxBulletsPerBlock),
  }));

export const scoreUiConsultationContext = (
  manifest: UiConsultationGroundingManifest,
  budget: UiConsultationBudget = DEFAULT_UI_CONSULTATION_BUDGET,
): UiConsultationBudgetReport => {
  const sectionEstimates = manifestSectionEstimates(manifest);
  const totalEstimatedTokens = Object.values(sectionEstimates).reduce((sum, value) => sum + value, 0);
  const manifestHash = stableHash(stableStringify(manifest));

  return UiConsultationBudgetReportSchema.parse({
    schemaVersion: 1,
    totalEstimatedTokens,
    softTokenTarget: budget.softTokenTarget,
    hardTokenCap: budget.hardTokenCap,
    withinSoftTarget: totalEstimatedTokens <= budget.softTokenTarget,
    withinHardCap: totalEstimatedTokens <= budget.hardTokenCap,
    trimmedSections: [],
    sectionEstimates,
    manifestHash,
  });
};

export const compressUiConsultationContext = (
  manifest: UiConsultationGroundingManifest,
  budget: UiConsultationBudget = DEFAULT_UI_CONSULTATION_BUDGET,
): {
  manifest: UiConsultationGroundingManifest;
  report: UiConsultationBudgetReport;
} => {
  let nextManifest = UiConsultationGroundingManifestSchema.parse(stableValue(manifest));
  const trimmedSections: string[] = [];

  const currentEstimate = () =>
    Object.values(manifestSectionEstimates(nextManifest)).reduce((sum, value) => sum + value, 0);

  if (currentEstimate() > budget.hardTokenCap && nextManifest.runtimeDigest.visibleTextBlocks.length > 8) {
    nextManifest = UiConsultationGroundingManifestSchema.parse({
      ...nextManifest,
      runtimeDigest: {
        ...nextManifest.runtimeDigest,
        visibleTextBlocks: nextManifest.runtimeDigest.visibleTextBlocks.slice(0, 8),
      },
    });
    trimmedSections.push('runtimeDigest.visibleTextBlocks');
  }

  if (currentEstimate() > budget.hardTokenCap && nextManifest.astDigest.nodes.length > 16) {
    nextManifest = UiConsultationGroundingManifestSchema.parse({
      ...nextManifest,
      astDigest: {
        ...nextManifest.astDigest,
        nodes: nextManifest.astDigest.nodes.slice(0, 16),
      },
    });
    trimmedSections.push('astDigest.nodes');
  }

  if (
    currentEstimate() > budget.hardTokenCap
    && nextManifest.aceBlocks.some((block) => block.bullets.length > 8)
  ) {
    nextManifest = UiConsultationGroundingManifestSchema.parse({
      ...nextManifest,
      aceBlocks: trimAceBlockBullets(nextManifest.aceBlocks, 8),
    });
    trimmedSections.push('aceBlocks.bullets');
  }

  if (
    currentEstimate() > budget.hardTokenCap
    && nextManifest.aceBlocks.some((block) => block.bullets.length > 3)
  ) {
    nextManifest = UiConsultationGroundingManifestSchema.parse({
      ...nextManifest,
      aceBlocks: trimAceBlockBullets(nextManifest.aceBlocks, 3),
    });
    trimmedSections.push('aceBlocks.bullets.compact');
  }

  if (currentEstimate() > budget.hardTokenCap) {
    const requirements = dedupeRequirements(nextManifest.requirements);
    if (requirements.length !== nextManifest.requirements.length) {
      nextManifest = UiConsultationGroundingManifestSchema.parse({
        ...nextManifest,
        requirements,
      });
      trimmedSections.push('requirements');
    }
  }

  const sectionEstimates = manifestSectionEstimates(nextManifest);
  const totalEstimatedTokens = Object.values(sectionEstimates).reduce((sum, value) => sum + value, 0);
  const manifestHash = stableHash(stableStringify(nextManifest));

  return {
    manifest: nextManifest,
    report: UiConsultationBudgetReportSchema.parse({
      schemaVersion: 1,
      totalEstimatedTokens,
      softTokenTarget: budget.softTokenTarget,
      hardTokenCap: budget.hardTokenCap,
      withinSoftTarget: totalEstimatedTokens <= budget.softTokenTarget,
      withinHardCap: totalEstimatedTokens <= budget.hardTokenCap,
      trimmedSections,
      sectionEstimates,
      manifestHash,
    }),
  };
};

export const hashUiConsultationBundle = (
  manifest: UiConsultationGroundingManifest,
): string => stableHash(stableStringify(manifest));
